'use client'

import type { Perrito } from '@/src/lib/api/types'
import { Button } from '@/src/components/ui/button'
import { useListPerritos } from '@/src/lib/hooks/useListPerritos'
import { ErrorBoundary } from 'next/dist/client/components/error-boundary'
import Image from 'next/image'
import Link from 'next/link'
import React, { Suspense } from 'react'
import { SilentErrorFallback } from '../common/silent-error-boundary'

// Menu item card component
function MenuItemCard({ item }: { item: Perrito }) {
  return (
    <div
      className="bg-white border border-gray-200 rounded-lg overflow-hidden shadow-md hover:shadow-lg hover:shadow-srperrito-dark/30 transition-shadow w-full max-w-[300px] mx-auto h-[360px] flex flex-col"
    >
      <div className="relative h-48 w-full bg-srperrito-dark/10 flex-shrink-0">
        {item.image
          ? (
              <Image
                src={item.image}
                alt={item.name}
                fill
                className="object-cover"
              />
            )
          : (
              <div className="absolute inset-0 flex items-center justify-center bg-srperrito-dark/10">
                <span className="text-srperrito-dark">Imagen no disponible</span>
              </div>
            )}
      </div>

      <div className="p-4 flex-grow flex flex-col">
        <div className="flex justify-between items-start mb-2">
          <h3 className="text-xl font-bold text-srperrito-dark">{item.name}</h3>
          <span className="text-lg font-bold text-srperrito-teal">
            {item.price.toFixed(2)}
            €
          </span>
        </div>
        <p className="text-gray-600 text-sm flex-grow">{item.description}</p>
      </div>
    </div>
  )
}

// Component that uses the hook
function MenuContent() {
  const { data: perritos } = useListPerritos()

  // Function to group perritos in rows of 3
  const groupPerritosInRows = (items: Perrito[]) => {
    const rows = []
    for (let i = 0; i < items.length; i += 3) {
      rows.push(items.slice(i, i + 3))
    }
    return rows
  }

  const perritoRows = groupPerritosInRows(perritos)

  return (
    <section className="py-20 bg-white">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <h2 id="perritos" className="text-3xl md:text-4xl font-bold text-srperrito-dark mb-4 font-serif">
            Nuestros Perritos Estrella
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Elaborados con salchichas artesanas, panes brioche y toppings de primera calidad.
            Cada bocado es una explosión de sabor.
          </p>
          <div className="bg-[#f3e8d7] flex items-center justify-center flex-col text-[15px] text-3xl md:text-4xl font-bold text-srperrito-dark mb-4 font-serif p-5 mt-5">
            <p>
              Aquí nadie se queda sin perrito!
            </p>
            <p>
              Tenemos pan sin gluten y opción vegetariana
            </p>
          </div>
        </div>

        <div id="perritos">
          {perritoRows.map((row, rowIndex) => (
            <div
              key={rowIndex}
              className={`grid gap-[15px] mb-8 justify-items-center px-2 max-w-5xl mx-auto ${
                row.length === 1
                  ? 'grid-cols-1 flex justify-center'
                  : 'grid-cols-1 md:grid-cols-3'
              }`}
            >
              {row.map(item => (
                <MenuItemCard key={item.id} item={item} />
              ))}
            </div>
          ))}
        </div>

        <div className="text-center mt-12">
          <Link href="/menu">
            <Button variant="primary" size="lg">
              Ver Menú Completo
            </Button>
          </Link>
        </div>
      </div>
    </section>
  )
}

// Fallback component for loading state
function MenuFallback() {
  return (
    <section className="py-20 bg-white">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-srperrito-dark mb-4 font-serif">
            Nuestros Perritos Estrella
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Elaborados con salchichas artesanas, panes brioche y toppings de primera calidad.
            Cada bocado es una explosión de sabor.
          </p>
        </div>
        <div className="text-center">
          <p className="text-gray-600">Cargando perritos...</p>
        </div>
      </div>
    </section>
  )
}

// Main component with Error Boundary and Suspense
function MenuPreview() {
  return (
    <ErrorBoundary errorComponent={SilentErrorFallback}>
      <Suspense fallback={<MenuFallback />}>
        <MenuContent />
      </Suspense>
    </ErrorBoundary>
  )
}

export default MenuPreview
